/**
 * Page to show the current account's funding sources.  This is a light wrapper around
 * the FundingSourceList component.
 * 
 * As of November 2023, we're showing cards on file on this page in a paper for cards on file.
 * Funding sources will only be shown if the expensing flag is on, or if funding sources exist for the account.
 */
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 
import PropTypes from 'prop-types';
import { Paper } from '@mui/material';
import withStyles from '@mui/styles/withStyles';

import { Grid, Tab, Tabs } from '@mui/material';
import { CreditCard, MonetizationOn, AccountBalance, Wallet } from '@mui/icons-material';
import ServerError from '../ui/ServerError';

import CCOnFileList from './CCOnFileList';
import FundingSourceList from './FundingSourceList';
import FundingSourceBudget from './FundingSourceBudget';
import FundingSourceProjectBudget from './FundingSourceProjectBudget';
import FundingSourceChargeLimit from './FundingSourceChargeLimit';
import PaymentRailsPayout from './PaymentRailsPayout';
import PageTitle from '../ui/PageTitle';
import AccountBudgetFetch from '../account/AccountBudgetFetch';
import LoadingSpinner from '../ui/LoadingSpinner';


const abEnvironment = new AbEnvironment();

const FundingSourcesQuery = graphql`
query FundingSourcesQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    ccCount: paymentSourceTsByAccountId(condition:{psType:0}){
      totalCount
    }
    achCount:paymentSourceTsByAccountId(condition:{psType:1}){
      totalCount
    }
    prCount:paymentSourceTsByAccountId(condition:{psType:3}){
      totalCount
    }
  }
}`;

const styleSheet = theme => ({
  paperInner: {
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 1154,
    marginTop: 16,
    marginBottom: 40
  },
  noPaperInner: {
    marginTop: 16,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 1200,
    marginTop: 16,
  },
  tabroot: {
    flexGrow: 1,
    margin: 0,
    maxWidth: 1200,
    //backgroundColor: theme.palette.primary.light,
    borderBottom: '2px solid #dedede'
  }
});
  
class FundingSources extends Component {

  constructor(props) {
    super(props);

    this.state = {
      tabValue: 0,
      useBudgets: 0,
      mountComplete: false,
      accountStatus: 2,
      account: null
    }
  }

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) { window.analytics.page(); }
    let value = localStorage.getItem('psInOut');
    let intValue = 0;
    if (value) {
      intValue = parseInt(value, 10);
    }

    let acctId = abEnvironment.getUserAttribute('a_id');
    AccountBudgetFetch.fetch(acctId).toPromise()
    .then(abf => {
      // For resellers, show budgets if budgets are on.
      //
      // For customers, show budgets if budgets are on and the autoTopupAmount is positive, or if there is
      // a wallet. This handles a legacy edge case in which a non-project unauth customer doesn't have any
      // paid subscriptions and therefore doesn't need wallet topups.
      let showBudgets = abf.accountTByAccountId.useBudgets;
      let walletCount = abf.accountTByAccountId.walletTsByAccountId.totalCount;
      if (walletCount === 0 && showBudgets >= 1) {
        // If a customer, and autoTopupAmount is not positive, override show budgets
        if (abf.accountTByAccountId.accountType === 4 && abf.accountTByAccountId.autoTopupAmount <= 0) {
          showBudgets = 0;
        }
      }

      if (showBudgets >= 1) {
        this.setState({useBudgets: showBudgets, tabValue: intValue, account: abf.accountTByAccountId, accountStatus: abf.accountTByAccountId.accountStatus,  mountComplete: true});
      } else {
        this.setState({tabValue: intValue, account: abf.accountTByAccountId, accountStatus: abf.accountTByAccountId.accountStatus, mountComplete: true});
      }
    })
    .catch(err => {
      this.setState({tabValue: intValue, mountComplete: true});
    })

  }

  handleTabChange = (event, value) => {
  
    localStorage.setItem('psInOut', value);
    this.setState({ tabValue: value });
  }

  render() {
    const { isAuthenticated, isUnauthenticatedCustomer, isAuthenticatedCustomer, isAuthenticatedReseller, isAuthenticatedVendor, needAssent, logError} = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else {
      if (!this.state.mountComplete) {
        return (
          <LoadingSpinner />
        );
      }

      let acctId = abEnvironment.getUserAttribute('a_id');
      let acctType = abEnvironment.getUserAttribute('a_aType');

      let title = 'My funding';
      let subTitle = 'How I pay for my subscriptions';
      let fundTab = 'Funding sources';
      let topUpTab = 'Budget';
      let payOutTab = 'Payout methods';

      if (!isUnauthenticatedCustomer() && !isAuthenticatedCustomer ()) {
        if (isAuthenticatedVendor()) {
          title = 'My funding sources and payout methods';
          subTitle = 'How will I pay for rebate programs?';
          // fundTab = 'Pay for rebate programs'
        } else {
          title = 'My funding sources and payout methods';
          subTitle = 'How will I pay for my subscriptions and receive payouts?';
        }
      }

      // Topup behavior depends on budget settings
      let showTopup = false;
      let showTopupRestrictedUse = false;

      if (this.state.useBudgets >= 1) {
        showTopup = true;
      }

      if (isAuthenticatedReseller() && !showTopup) {
        showTopupRestrictedUse = true;
      }

      let tabbedView = true;
      if (!showTopup && (isUnauthenticatedCustomer() || isAuthenticatedCustomer())) {
        tabbedView = false;
      }
      if (!showTopup && showTopupRestrictedUse) {
        tabbedView = true;
      }

      let accountStatus = parseInt(this.state.accountStatus, 10);

      let hasWallet = false;
      if (this.state.account.walletTsByAccountId.totalCount > 0) {
        hasWallet = true;
      }

      if (isAuthenticatedReseller() && showTopup && hasWallet) {
        topUpTab = 'Balances';
      } else if (isAuthenticatedReseller() && showTopup && !hasWallet) {
        topUpTab = "Expense wallet";
      } else if ((isAuthenticatedCustomer() || isUnauthenticatedCustomer()) && showTopup) {
        topUpTab = "Expense wallet";
      }

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={FundingSourcesQuery}
          variables={{accountId: acctId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              let ccCount = props.accountTByAccountId.ccCount.totalCount;
              let achCount = props.accountTByAccountId.achCount.totalCount;
              let prCount = props.accountTByAccountId.prCount.totalCount;
 
              if (tabbedView) {
                return (
                  <React.Fragment>
                    <PageTitle title={title} subtitle={subTitle}/>
                    <Paper className={classes.paper}>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={this.state.tabValue}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{root: classes.tabroot, scrollButtons: classes.tabScrollButton}}
                          >
                            <Tab label={fundTab} icon={<CreditCard />}
                              classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                            />
                            
                            {/* Project/wallet is on, so there is an account balance and one or more wallets, so say Balances */}
                            { isAuthenticatedReseller() && showTopup && hasWallet &&
                            <Tab label="Balances" icon={<AccountBalance />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}

                            {/* Project/wallet is off, so there is just an account balance, but we're introducing wallet, so say Wallet */}
                            { isAuthenticatedReseller() && showTopup && !hasWallet &&
                            <Tab label="Wallet" icon={<Wallet />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}

                            {/* Legacy */}
                            { isAuthenticatedReseller() && !showTopup &&
                            <Tab label={"Charge Limits"} icon={<AccountBalance />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}

                            { (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) && showTopup &&
                            <Tab label={topUpTab} icon={<Wallet />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}

                            { (isAuthenticatedCustomer() || isUnauthenticatedCustomer()) && !showTopup && showTopupRestrictedUse &&
                            <Tab label={"Charge Limits"} icon={<AccountBalance />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}

                            { !isUnauthenticatedCustomer() && !isAuthenticatedCustomer () &&
                            <Tab label={payOutTab} icon={<MonetizationOn />}
                              classes={{
                                root: classes.tab,
                                wrapper: classes.labelWrapped,
                              }}
                            />}
                          </Tabs>
                        </Grid>
                      </Grid>
          
                      {/* In paper presentation
                      {this.state.tabValue === 0 &&
                      <Paper className={classes.paperInner}>
                        <FundingSourceList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history} accountStatus={accountStatus}/>
                      </Paper>}
                      */}
                      {this.state.tabValue === 0 && (ccCount > 0 || achCount > 0) &&
                      <React.Fragment>
                        <Paper className={classes.paper}>
                          <CCOnFileList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history}/>
                        </Paper>
                        <Paper className={classes.paper}>
                          <FundingSourceList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history} accountStatus={accountStatus}/>
                        </Paper>
                      </React.Fragment>}

                      {this.state.tabValue === 0 && ccCount === 0 && achCount === 0 &&
                      <React.Fragment>
                        <div className={classes.noPaperInner}>
                          <CCOnFileList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history}/>
                         </div>
                      </React.Fragment>}

                      {this.state.tabValue === 1 && !isAuthenticatedVendor() && showTopup && !hasWallet &&
                      <FundingSourceBudget purpose={0} accountId={acctId} accountType={acctType} useBudgets={this.state.useBudgets} auth={this.props.auth} history={this.props.history}  accountStatus={accountStatus}/>}

                      {this.state.tabValue === 1 && !isAuthenticatedVendor() && showTopup && hasWallet &&
                      <FundingSourceProjectBudget purpose={0} accountId={acctId} accountType={acctType} useBudgets={this.state.useBudgets} auth={this.props.auth} history={this.props.history}  accountStatus={accountStatus}/>}

                      {this.state.tabValue === 1 && !isAuthenticatedVendor() && !showTopup && showTopupRestrictedUse &&
                      <FundingSourceChargeLimit purpose={0} accountId={acctId} accountType={acctType} useBudgets={this.state.useBudgets} auth={this.props.auth} history={this.props.history}  accountStatus={accountStatus}/>}

                      {this.state.tabValue === 1 && (isAuthenticatedVendor() || (!showTopup && !showTopupRestrictedUse)) &&
                      <PaymentRailsPayout cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history} accountStatus={accountStatus}/>}

                      {this.state.tabValue === 2 &&
                      <PaymentRailsPayout cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history} accountStatus={accountStatus}/>}

                    </Paper>
                  </React.Fragment>
                );
              } else {
                /* Show cards on file list, and funding source list if relevant */
                return (
                  <React.Fragment>
                    <PageTitle title={title} subtitle={subTitle}/>
                    <Paper className={classes.paper}>
                      <CCOnFileList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history}/>
                    </Paper>
                    { (ccCount > 0 || achCount > 0) &&
                    <Paper className={classes.paper}>
                      <FundingSourceList purpose={0} cp="myfunding" accountId={acctId} auth={this.props.auth} history={this.props.history} accountStatus={accountStatus}/>
                    </Paper>}
                  </React.Fragment>
                );    
              }
            }
            return <div><LoadingSpinner/></div>
          }}
        />
      );
    }
  }
}

FundingSources.propTypes = {
  classes: PropTypes.object.isRequired,
};
    
export default withStyles(styleSheet)(FundingSources);